<script>
import AuthStore from '@stores/AuthStore'
import FabButton from '@components/FabButton'
import ResourceMaterialsRequests from '@routes/Vue/ResourceMaterialsRequests'
import { marked } from 'marked'

const videoEmbedRenderer = {
  link (href, title, text) {
    if (!href) {
      return text
    }

    let match

    // YouTube: https://www.youtube.com/watch?v=ID or https://youtu.be/ID
    match = href.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
    if (match) {
      return `<iframe width="100%" style="aspect-ratio: 16 / 9;" src="https://www.youtube.com/embed/${match[1]}?rel=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
    }

    // Vimeo: https://vimeo.com/ID
    match = href.match(/vimeo\.com\/(\d+)/)
    if (match) {
      return `<iframe width="100%" style="aspect-ratio: 16 / 9;" src="https://player.vimeo.com/video/${match[1]}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
    }

    // Cross-link: resource://PAGE_ID
    const crossLinkMatch = href.match(/^resource:\/\/(.+)/)
    if (crossLinkMatch) {
      const pageId = crossLinkMatch[1]
      return `<a href="#" data-resource-page-id="${pageId}" class="resource-cross-link">${text}</a>`
    }

    // Default link rendering
    const titleAttr = title ? ` title="${title}"` : ''
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
  },
}

marked.setOptions({
  breaks: true,
  gfm: true,
})
marked.use({ renderer: videoEmbedRenderer })

export default {
  name: 'ResourceWidget',
  components: { FabButton },
  data () {
    return {
      open: false,
      tree: [],
      navigationStack: [], // stack of folder nodes for back navigation
      currentItems: [], // items in current folder
      currentPage: null, // currently viewed page content
      loading: false,
    }
  },
  computed: {
    ...AuthStore.mapState('currentUser'),
    isImpersonating () {
      return !!this.currentUser.impersonatedById
    },
    showResourceWidgetButton () {
      if (this.isImpersonating) return false
      return !!this.currentUser.showResourceMaterials
    },
    currentFolder () {
      return this.navigationStack.length > 0
        ? this.navigationStack[this.navigationStack.length - 1]
        : null
    },
    headerTitle () {
      if (this.currentFolder) return this.currentFolder.title
      return 'Resource Center'
    },
    canGoBack () {
      return this.navigationStack.length > 0
    },
  },
  methods: {
    toggle () {
      this.open = !this.open
      if (this.open && this.tree.length === 0) {
        this.fetchTree()
      }
    },
    close () {
      this.open = false
    },
    visibleItems (items) {
      return items.filter(item => !item.hiddenInWidget)
    },
    fetchTree () {
      this.loading = true
      ResourceMaterialsRequests.tree()
        .then(tree => {
          this.tree = tree
          this.currentItems = this.visibleItems(tree)
          this.loading = false
        })
        .catch(() => { this.loading = false })
    },
    navigateToFolder (folder) {
      this.currentPage = null
      this.navigationStack.push(folder)
      this.currentItems = this.visibleItems(folder.children || [])
    },
    openPage (page) {
      this.loading = true
      ResourceMaterialsRequests.get({ id: page.id })
        .then(fullPage => {
          this.currentPage = {
            ...fullPage,
            renderedContent: marked(fullPage.content || ''),
          }
          this.loading = false
        })
        .catch(() => { this.loading = false })
    },
    closePageModal () {
      this.currentPage = null
    },
    onPageContentClick (event) {
      const link = event.target.closest('[data-resource-page-id]')
      if (link) {
        event.preventDefault()
        const pageId = link.dataset.resourcePageId
        this.openPage({ id: pageId })
      }
    },
    goBack () {
      this.navigationStack.pop()
      if (this.currentFolder) {
        this.currentItems = this.visibleItems(this.currentFolder.children || [])
      } else {
        this.currentItems = this.visibleItems(this.tree)
      }
    },
    goHome () {
      this.navigationStack = []
      this.currentItems = this.visibleItems(this.tree)
      this.currentPage = null
    },
    onItemClick (item) {
      if (item.nodeType === 'folder') {
        this.navigateToFolder(item)
      } else {
        this.openPage(item)
      }
    },
  },
}
</script>

<template>
  <div class="resource-widget">
    <transition name="resource-panel">
      <div v-if="open" class="resource-widget__panel">
        <div class="resource-widget__header">
          <button v-if="canGoBack" class="resource-widget__back" @click="goBack">
            <Icon name="arrowLeft" size="small"/>
          </button>
          <h3 class="resource-widget__title">{{ headerTitle }}</h3>
          <Button :normal="false" class="resource-widget__close" @click="close">
            <Icon name="close" size="small"/>
          </Button>
        </div>

        <div class="resource-widget__body">
          <LoadingIndicator v-if="loading" size="mobile" class="resource-widget__loading"/>

          <!-- Folder/menu list view -->
          <template v-else>
            <div v-if="currentItems.length === 0" class="resource-widget__empty">
              No materials available
            </div>
            <div v-else class="resource-widget__list">
              <button
                v-for="item in currentItems"
                :key="item.id"
                class="resource-widget__menu-item"
                @click="onItemClick(item)"
              >
                <Icon :name="item.nodeType === 'folder' ? 'filetreeDir' : 'document'" size="small" class="resource-widget__menu-icon"/>
                <div class="resource-widget__menu-text">
                  <span class="resource-widget__menu-label">{{ item.title }}</span>
                  <span v-if="item.description" class="resource-widget__menu-description">{{ item.description }}</span>
                </div>
                <Icon v-if="item.nodeType === 'folder'" name="chevronRight" size="small" class="resource-widget__menu-arrow"/>
              </button>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <FabButton
      v-if="showResourceWidgetButton"
      :icon="open ? 'close' : 'help'"
      class="resource-widget__fab"
      :class="{ 'resource-widget__fab--active': open }"
      @click="toggle"
    />

    <Modal v-if="currentPage" normal closeOnBlurClick @close="closePageModal">
      <template #modalHeader>
        <h2 class="resource-widget__modal-title">{{ currentPage.title }}</h2>
      </template>
      <template #modalBody>
        <div class="resource-widget__page-content" @click="onPageContentClick" v-html="currentPage.renderedContent"/>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.resource-widget {
  bottom: 24px;
  position: fixed;
  right: 24px;
  z-index: 99999;
}

.resource-widget__fab {
  &--active {
    background-color: $primary-color;

    &:hover {
      background-color: $primary-color;
    }
  }
}

.resource-widget__panel {
  background: $WHITE;
  border-radius: $radius-normal;
  bottom: 100px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-height: 520px;
  overflow: hidden;
  position: absolute;
  right: 0;
  width: 350px;
}

.resource-widget__header {
  align-items: center;
  border-bottom: $br-light;
  display: flex;
  gap: 8px;
  padding: 14px 16px;
}

.resource-widget__back {
  background: none;
  border: none;
  color: $fc-html-light;
  cursor: pointer;
  display: flex;
  padding: 4px;

  &:hover {
    color: $fc-html;
  }
}

.resource-widget__title {
  @include ellipsize;

  color: $fc-html;
  flex: 1;
  font-size: 18px;
  font-weight: $fw-bold;
  margin: 0;
}

.resource-widget__close {
  align-items: center;
  border-radius: 50%;
  color: $fc-html-light;
  display: flex;
  height: 28px;
  justify-content: center;
  padding: 4px;
  width: 28px;

  &:hover {
    color: $fc-html;
  }
}

.resource-widget__body {
  flex: 1;
  overflow-y: auto;
}

.resource-widget__loading {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.resource-widget__list {
  display: flex;
  flex-direction: column;
}

.resource-widget__menu-item {
  align-items: center;
  background: none;
  border: none;
  border-bottom: $br-light;
  cursor: pointer;
  display: flex;
  gap: 10px;
  height: 87px;
  padding: 12px 16px;
  text-align: left;
  transition: background-color 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: $tertiary-color;
  }
}

.resource-widget__menu-icon {
  fill: $fc-html-light;
  flex-shrink: 0;
}

.resource-widget__menu-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.resource-widget__menu-label {
  color: $fc-html;
  font-size: 18px;
}

.resource-widget__menu-description {
  color: rgb(106, 108, 117);
  font-size: 12px;
  font-weight: 400;
  margin-top: 2px;
  text-transform: uppercase;
}

.resource-widget__menu-arrow {
  fill: $fc-html-light;
  flex-shrink: 0;
}

.resource-widget__page-content {
  color: $fc-html;
  font-size: $fs-large;
  line-height: $lh-normal;
  padding: 16px 20px;
  word-wrap: break-word;

  & ::v-deep img {
    border-radius: $radius-normal;
    margin: 8px 0;
    max-width: 100%;
  }

  & ::v-deep h1,
  & ::v-deep h2,
  & ::v-deep h3 {
    margin-bottom: 8px;
    margin-top: 16px;
  }

  & ::v-deep p {
    margin-bottom: 8px;
  }

  & ::v-deep a {
    align-items: center;
    background: $bg-gradient;
    border: none;
    border-radius: $radius-rounded;
    color: $WHITE;
    cursor: pointer;
    display: inline-flex;
    font-size: $fs-normal;
    font-weight: $fw-regular;
    height: $button-full-height;
    justify-content: center;
    line-height: $button-full-line-height;
    margin: 4px 0;
    padding: 0 $button-full-padding-horizontal;
    text-decoration: none;
    transition: all ease-out 0.3s;
    white-space: nowrap;

    &:hover,
    &:focus {
      background: $bg-gradient-light;
      text-decoration: none;
    }
  }

  & ::v-deep .resource-cross-link {
    background: $bg-gradient;
    color: $WHITE;
  }

  & ::v-deep iframe,
  & ::v-deep video {
    aspect-ratio: 16 / 9;
    border-radius: $radius-normal;
    height: auto !important;
    margin: 8px 0;
    max-width: 100% !important;
    width: 100% !important;
  }
}

.resource-widget__modal-title {
  font-size: $fs-large;
  font-weight: $fw-bold;
  margin: 0;
}

.resource-widget__empty {
  color: $fc-html-light;
  font-size: $fs-secondary;
  padding: 40px 20px;
  text-align: center;
}

// Panel slide transition
.resource-panel-enter-active,
.resource-panel-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.resource-panel-enter,
.resource-panel-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

// Icon rotate transition
.rotate-enter-active,
.rotate-leave-active {
  position: absolute;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.rotate-enter,
.rotate-leave-to {
  opacity: 0;
  transform: rotate(90deg);
}
</style>
